<template>
  <v-col cols="12" class="landing-col px-0">
    <div class="ios-section">
      <div class="ios-section-header">
        <span class="ios-section-title">{{ searchQuery.length >= 2 ? $t('publicEvents.searchResults', { q: searchQuery }) : $t('publicEvents.title') }}</span>
      </div>

      <!-- Loading state -->
      <div v-if="loading && events.length === 0" class="text-center py-8">
        <v-progress-circular indeterminate color="grey" size="32" />
      </div>

      <!-- Empty state -->
      <div v-else-if="!loading && events.length === 0" class="empty-state">
        <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-calendar-blank-outline</v-icon>
        <div class="text-body-1 text-grey-darken-1">{{ $t('publicEvents.empty') }}</div>
      </div>

      <!-- Events list -->
      <template v-else>
        <NextEvents
          :events="events"
          :showLeagueLogo="true"
          :showTeamLogos="true"
          :showLeagueHeader="true"
          :hideActions="true"
          @navigate="onEventClick"
        />

        <!-- Load more -->
        <div v-if="hasMore" style="padding: 0 16px; margin-top: 8px;">
          <button class="load-more-btn" :disabled="loadingMore" @click="loadMore">
            <v-progress-circular v-if="loadingMore" indeterminate size="16" width="2" />
            <template v-else>{{ $t('loadMore') }}</template>
          </button>
        </div>

        <!-- Page info -->
        <div v-if="events.length > 0" class="text-center text-caption text-grey pb-2">
          {{ $t('publicEvents.shown', { shown: events.length, total }) }}
        </div>
      </template>
    </div>
  </v-col>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axiosInstance from '@/services/axiosInstance'
import NextEvents from '@/components/LandingComponents/NextEvents.vue'
import type { IEvent } from '@/types'

interface EventFilters {
  city: string
  ageGroup: string[]
  recruitment: string | null
}

const props = withDefaults(defineProps<{
  /** When set, fetches events matching this search query instead of all public upcoming */
  searchQuery?: string
  /** Optional filters for city, ageGroup, recruitment */
  filters?: EventFilters
}>(), {
  searchQuery: '',
  filters: () => ({ city: '', ageGroup: [], recruitment: null }),
})

const router = useRouter()

const PAGE_SIZE = 10

const events = ref<IEvent[]>([])
const loading = ref(false)
const loadingMore = ref(false)
const page = ref(1)
const total = ref(0)
const totalPages = ref(0)
const hasMore = ref(false)

async function fetchEvents(pageNum: number, append = false) {
  try {
    if (append) {
      loadingMore.value = true
    } else {
      loading.value = true
    }

    const isSearch = props.searchQuery.length >= 2
    const url = isSearch ? '/events/public-search' : '/events/public-upcoming'
    const params: Record<string, any> = { page: pageNum, limit: PAGE_SIZE }
    if (isSearch) params.q = props.searchQuery

    // Add filter params
    if (props.filters.city) params.city = props.filters.city
    if (props.filters.ageGroup.length) params.ageGroup = props.filters.ageGroup.join(',')
    if (props.filters.recruitment !== null) params.recruitment = props.filters.recruitment

    const { data } = await axiosInstance.get(url, { params })

    if (append) {
      events.value = [...events.value, ...data.events]
    } else {
      events.value = data.events
    }

    total.value = data.total
    totalPages.value = data.totalPages
    page.value = data.page
    hasMore.value = data.page < data.totalPages
  } catch (error) {
    console.error('Error loading public events:', error)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

function loadMore() {
  fetchEvents(page.value + 1, true)
}

function onEventClick(event: IEvent) {
  const eventPath = `/event/${event._id}`
  if (event._league) {
    // League events are publicly accessible
    router.push(eventPath)
  } else {
    router.push({ path: '/login', query: { returnTo: eventPath } })
  }
}

// Watch searchQuery – reset and re-fetch when it changes
watch(() => props.searchQuery, () => {
  page.value = 1
  fetchEvents(1)
})

// Watch filters – reset and re-fetch when they change
watch(() => props.filters, () => {
  page.value = 1
  fetchEvents(1)
}, { deep: true })

onMounted(() => {
  fetchEvents(1)
})
</script>

<style lang="scss" scoped>
.landing-col {
  padding-top: 0;
  padding-bottom: 0;

  @media (max-width: 959px) {
    padding: 0 !important;
  }
}

.ios-section {
  margin-bottom: 24px;
}



.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  text-align: center;
}
</style>
